import {
  AdsClick,
  AutoAwesome,
  BarChart,
  Close as CloseIcon,
  GraphicEq,
  HelpOutline,
  Http,
  LocalOffer,
  PlaylistAdd,
  Search,
  Sync,
  Timeline,
  Warning,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Bot, Image as LucideImage } from "lucide-react";
import { SvgIcon } from "@mui/material";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";
import { LIST_ACTION_TYPE_ADD_ROWS, LIST_ACTION_TYPE_ADD_TAGS, LIST_ACTION_TYPE_CRM_SYNC, LIST_ACTION_TYPE_CRM_LOOKUP, LIST_ACTION_TYPE_APOLLO_ENRICH, LIST_ACTION_TYPE_FINDYMAIL_ENRICH, LIST_ACTION_TYPE_AUDIENCELAB_ENRICH, LIST_ACTION_TYPE_BETTERCONTACT_ENRICH, LIST_ACTIONS, ListAction } from "../../constants";
import { INTEGRATION_LOGOS } from "../integrations/constants";
import ListActionAddSequenceForm from "./ListActionAddSequenceForm";
import ListActionAddTagsForm from "./ListActionAddTagsForm";
import ListActionAddToListForm from "./ListActionAddToListForm";
import ListActionAgentForm from "./ListActionAgentForm";
import ListActionAiTextForm from "./ListActionAiTextForm";
import ListActionAiImageForm from "./ListActionAiImageForm";
import ListActionAiAudioForm from "./ListActionAiAudioForm";
import ListActionApolloEnrichForm from "./ListActionApolloEnrichForm";
import ListActionFindymailEnrichForm from "./ListActionFindymailEnrichForm";
import ListActionBetterContactEnrichForm from "./ListActionBetterContactEnrichForm";
import ListActionAudiencelabEnrichForm from "./ListActionAudiencelabEnrichForm";
import ListActionBrowserTaskForm from "./ListActionBrowserTaskForm";
import ListActionCrmSyncForm from "./ListActionCrmSyncForm";
import ListActionCrmLookupForm from "./ListActionCrmLookupForm";
import ListActionConditions from "./ListActionConditions";
import ListActionHttpRequestForm from "./ListActionHttpRequestForm";

// Wrapper to make Lucide Bot icon compatible with MUI
const BotIcon = (props: any) => (
  <SvgIcon {...props}>
    <Bot />
  </SvgIcon>
);

// Wrapper to make Lucide Image icon compatible with MUI
const ImageIcon = (props: any) => (
  <SvgIcon {...props}>
    <LucideImage />
  </SvgIcon>
);

// MUI icon mapping for list actions
const MUI_ICONS: Record<string, React.ElementType> = {
  SmartToy: BotIcon,
  Timeline,
  Http,
  PlaylistAdd,
  AutoAwesome,
  Image: ImageIcon,
  GraphicEq,
  Search,
  Sync,
  AdsClick,
  BarChart,
  LocalOffer,
};

// Helper component to render action icon (MUI icon or integration logo)
const ActionIcon: React.FC<{ action: ListAction; size?: number }> = ({ action, size = 24 }) => {
  if (action.icon.type === "logo") {
    const logoSrc = INTEGRATION_LOGOS[action.icon.name];
    if (logoSrc) {
      return (
        <Avatar
          src={logoSrc}
          alt={action.title}
          sx={{ width: size, height: size }}
        />
      );
    }
  }

  // MUI icon
  const IconComponent = MUI_ICONS[action.icon.name];
  if (IconComponent) {
    return <IconComponent sx={{ fontSize: size, color: "text.secondary" }} />;
  }

  return null;
};

const ResearchAssistantDialog = ({
  open,
  onClose,
  headers,
  listId,
  title,
  type,
  companyId,
  getToken,
  eventListener,
  currentPlan,
  options,
  existingNames = [],
}: {
  open: boolean;
  onClose: () => void;
  headers: Array<any>;
  listId: string;
  title: string;
  type: string;
  companyId: string;
  getToken: Function;
  eventListener: Function;
  currentPlan: string;
  options: any;
  existingNames?: string[];
}) => {
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState("");
  const [autoRunEnabled, setAutoRunEnabled] = useState(false);
  const [conditions, setConditions] = useState<any>({
    combinator: "AND",
    filters: [],
    groups: [],
  });
  const [actionParameters, setActionParameters] = useState<any>({});
  const [existingRecord, setExistingRecord] = useState(false);
  const [validForm, setValidForm] = useState(false);
  const [subFormValid, setSubFormValid] = useState(false);
  const [nameError, setNameError] = useState("");

  const handleClose = () => {
    setActionId("");
    setActionParameters({});
    setAutoRunEnabled(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validForm) return;
    setLoading(true);
    const currentToken = await getToken();
    axios
      .post(
        `${hostname}/lists/${listId}/action`,
        {
          companyId,
          actionParameters,
          actionId,
          autoRunEnabled,
          conditions,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then(() => {
        setActionId("");
        setActionParameters({});
        setAutoRunEnabled(false);
        setConditions({
          combinator: "AND",
          filters: [],
          groups: [],
        });
        setValidForm(false);
        onClose();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (options) {
      console.log("Options received in dialog:", options);
      setActionId(options.actionId || "");
      setActionParameters({
        ...options.actionParameters,
      });
      setAutoRunEnabled(options.autoRunEnabled);
      setConditions(
        options.conditions || {
          combinator: "AND",
          filters: [],
          groups: [],
        }
      );
      setExistingRecord(options.actionId !== undefined);
      setValidForm(false);
    }
  }, [options]);

  // Validate name against existing names
  useEffect(() => {
    const currentName = actionParameters.name || "";
    if (currentName.trim()) {
      const normalizedName = currentName.trim().toLowerCase();
      // When editing, exclude the current action from the duplicate check
      const namesToCheck = existingRecord
        ? existingNames.filter((name) => {
            // Get the original name from the options if available
            const originalName = options?.actionParameters?.name || "";
            return name.toLowerCase() !== originalName.toLowerCase();
          })
        : existingNames;

      const isDuplicate = namesToCheck.some(
        (existingName) => existingName.toLowerCase() === normalizedName
      );
      if (isDuplicate) {
        setNameError("A column or action with this name already exists");
      } else {
        setNameError("");
      }
    } else {
      setNameError("");
    }
  }, [actionParameters.name, existingNames, existingRecord, options]);

  // Overall form validation that checks both name and sub-form validation
  useEffect(() => {
    const hasName =
      actionParameters.name && actionParameters.name.trim().length > 0;
    setValidForm(hasName && subFormValid && !nameError);
  }, [actionParameters.name, subFormValid, nameError]);

  const actionDef = LIST_ACTIONS.find((a) => a.type === type);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {actionDef && <ActionIcon action={actionDef} />}
          {title}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: "16px" }}>
        <div style={{ marginBottom: "24px" }}>
          <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Action Name
            <Tooltip title="This name becomes the column header where results are stored. Use a clear, descriptive name like 'Email Intro' or 'Company Research'.">
              <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
            </Tooltip>
          </Typography>
          <Typography
            variant="body2"
            style={{ marginTop: "8px", marginBottom: "8px" }}
          >
            Give your action a descriptive name to identify it easily
          </Typography>
          <TextField
            fullWidth
            value={actionParameters.name || ""}
            onChange={(e) =>
              setActionParameters({ ...actionParameters, name: e.target.value })
            }
            placeholder="Enter action name"
            variant="outlined"
            error={!!nameError}
            helperText={nameError}
          />
        </div>
        <div>
          <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Enable Auto-Run
            <Tooltip title={actionParameters.action === LIST_ACTION_TYPE_ADD_ROWS
              ? "When enabled, this action runs automatically once per day to find and add new rows."
              : "When enabled, this action runs automatically whenever new rows are added to the list, processing them immediately."}>
              <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2" style={{ marginTop: "8px" }}>
            {actionParameters.action === LIST_ACTION_TYPE_ADD_ROWS
              ? "Auto-Run executes this action daily"
              : "Auto-Run executes this action on new rows immediately."}
          </Typography>
          <Switch
            checked={autoRunEnabled}
            onChange={(e) => setAutoRunEnabled(!autoRunEnabled)}
            color="primary"
          />
        </div>

        {actionParameters.action !== LIST_ACTION_TYPE_ADD_ROWS &&
          type !== LIST_ACTION_TYPE_ADD_TAGS && (
            <ListActionConditions
              headers={headers}
              conditions={conditions}
              setConditions={setConditions}
            />
          )}

        {/* <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "24px",
          }}
        >
          <Button variant="outlined" startIcon={<FolderOpen />}>
            Browse Templates (4)
          </Button>
          <Button variant="outlined" startIcon={<Save />}>
            Save as Template
          </Button>
        </div> */}
        {type === "add_sequence" && (
          <ListActionAddSequenceForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            getToken={getToken}
            setValidForm={setSubFormValid}
            companyId={companyId}
            headers={headers}
          />
        )}
        {type === "agent" && (
          <ListActionAgentForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            currentPlan={currentPlan}
            headers={headers}
            setValidForm={setSubFormValid}
            existingRecord={existingRecord}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
        {type === "http_request" && (
          <ListActionHttpRequestForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            headers={headers}
            setValidForm={setSubFormValid}
            existingRecord={existingRecord}
          />
        )}
        {type === "add_to_list" && (
          <ListActionAddToListForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            companyId={companyId}
            getToken={getToken}
            setValidForm={setSubFormValid}
            listId={listId}
          />
        )}
        {type === LIST_ACTION_TYPE_ADD_TAGS && (
          <ListActionAddTagsForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            companyId={companyId}
            getToken={getToken as () => Promise<string>}
            setValidForm={setSubFormValid}
            headers={headers}
          />
        )}
        {type === "ai_text" && (
          <ListActionAiTextForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            currentPlan={currentPlan}
            headers={headers}
            setValidForm={setSubFormValid}
            existingRecord={existingRecord}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
        {type === "ai_image" && (
          <ListActionAiImageForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            currentPlan={currentPlan}
            headers={headers}
            setValidForm={setSubFormValid}
            existingRecord={existingRecord}
            getToken={getToken}
            companyId={companyId}
          />
        )}
        {type === "ai_audio" && (
          <ListActionAiAudioForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            currentPlan={currentPlan}
            headers={headers}
            setValidForm={setSubFormValid}
            existingRecord={existingRecord}
            getToken={getToken}
            companyId={companyId}
          />
        )}
        {type === "browser_task" && (
          <ListActionBrowserTaskForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            headers={headers}
            setValidForm={setSubFormValid}
            existingRecord={existingRecord}
            getToken={getToken}
            companyId={companyId}
          />
        )}
        {type === LIST_ACTION_TYPE_CRM_SYNC && (
          <ListActionCrmSyncForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            setValidForm={setSubFormValid}
            getToken={getToken}
            companyId={companyId}
          />
        )}
        {type === LIST_ACTION_TYPE_CRM_LOOKUP && (
          <ListActionCrmLookupForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            setValidForm={setSubFormValid}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
        {type === LIST_ACTION_TYPE_APOLLO_ENRICH && (
          <ListActionApolloEnrichForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            setValidForm={setSubFormValid}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
        {type === LIST_ACTION_TYPE_FINDYMAIL_ENRICH && (
          <ListActionFindymailEnrichForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            setValidForm={setSubFormValid}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
        {type === LIST_ACTION_TYPE_AUDIENCELAB_ENRICH && (
          <ListActionAudiencelabEnrichForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            setValidForm={setSubFormValid}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
        {type === LIST_ACTION_TYPE_BETTERCONTACT_ENRICH && (
          <ListActionBetterContactEnrichForm
            actionParameters={actionParameters}
            setActionParameters={setActionParameters}
            setValidForm={setSubFormValid}
            getToken={getToken as () => Promise<string>}
            companyId={companyId}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} style={{ marginRight: "8px" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled
          sx={{ display: validForm ? "none" : "block" }}
        >
          <Warning fontSize="small" style={{ marginRight: "4px" }} />
          Missing Fields
        </Button>
        <LoadingButton
          sx={{ display: validForm ? "flex" : "none" }}
          loading={loading}
          onClick={handleSubmit}
          variant="outlined"
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ResearchAssistantDialog;
